import type { FunnelMetrics } from './metrics';
import { subStatusGroup, type SubStatusCatalogEntry } from './subStatus';

/** Distribución round-robin: siguiente asesor y nuevo índice. */
export function nextRoundRobin(
  advisorIds: readonly string[],
  lastIndex: number,
): { advisorId: string; index: number } | null {
  if (advisorIds.length === 0) return null;
  const index = ((lastIndex ?? -1) + 1) % advisorIds.length;
  return { advisorId: advisorIds[index]!, index };
}

/** "De cada 10 leads": los pesos de una regla deben sumar este total. */
export const WEIGHT_TOTAL = 10;

/**
 * Secuencia de reparto con pesos enteros ("de cada 10 leads, 8 para Ana y 2 para Luis"), bien
 * intercalada (round-robin ponderado suave). Sin pesos, o con todos iguales, es el orden de la lista.
 */
export function weightedSequence(
  advisorIds: readonly string[],
  weights?: Record<string, number> | null,
): string[] {
  const w = advisorIds.map((id) => Math.max(0, Math.floor(weights?.[id] ?? 1)));
  const total = w.reduce((s, n) => s + n, 0);
  if (total === 0) return [...advisorIds];
  const current = w.map(() => 0);
  const sequence: string[] = [];
  for (let k = 0; k < total; k++) {
    let best = -1;
    for (let i = 0; i < w.length; i++) {
      if (w[i] === 0) continue;
      current[i]! += w[i]!;
      if (best < 0 || current[i]! > current[best]!) best = i;
    }
    current[best]! -= total;
    sequence.push(advisorIds[best]!);
  }
  return sequence;
}

/** Siguiente asesor del reparto: el cursor recorre la secuencia (con pesos o pareja) y vuelve a empezar. */
export function nextWeighted(
  advisorIds: readonly string[],
  weights: Record<string, number> | null | undefined,
  lastIndex: number,
): { advisorId: string; index: number } | null {
  const sequence = weightedSequence(advisorIds, weights);
  if (sequence.length === 0) return null;
  const index = ((lastIndex ?? -1) + 1) % sequence.length;
  return { advisorId: sequence[index]!, index };
}

/**
 * Siguiente asesor del turno que cumple una condición (Lote 6 · I4, reparto con leads de varios proyectos): recorre
 * la secuencia desde el cursor y salta a quienes no la cumplen (p. ej. sin acceso al proyecto del lead); el cursor
 * queda en el elegido, igual que en `nextWeighted`. Null si nadie de la secuencia la cumple (el cursor no se mueve).
 */
export function nextWeightedFor(
  advisorIds: readonly string[],
  weights: Record<string, number> | null | undefined,
  lastIndex: number,
  accepts: (advisorId: string) => boolean,
): { advisorId: string; index: number } | null {
  const sequence = weightedSequence(advisorIds, weights);
  const n = sequence.length;
  for (let step = 1; step <= n; step++) {
    const index = ((((lastIndex ?? -1) + step) % n) + n) % n;
    const advisorId = sequence[index]!;
    if (accepts(advisorId)) return { advisorId, index };
  }
  return null;
}

/**
 * Vuelve a repartir las partes «de cada 10» entre los asesores que quedan conservando su proporción (método del
 * mayor resto, cada uno al menos 1): de {A: 6, B: 2, C: 2} sin A queda {B: 5, C: 5}; de {A: 4, B: 4, C: 2} sin A,
 * {B: 7, C: 3}. Si ya suman 10 no cambia nada; null si no queda nadie o son más de 10 (no hay reparto posible).
 */
export function rescaleWeights(weights: Record<string, number>): Record<string, number> | null {
  const entries = Object.entries(weights).filter(([, w]) => Number.isFinite(w) && w > 0);
  if (entries.length === 0 || entries.length > WEIGHT_TOTAL) return null;
  const total = entries.reduce((s, [, w]) => s + w, 0);
  if (total === WEIGHT_TOTAL) return Object.fromEntries(entries);
  const parts = entries.map(([id, w]) => {
    const exact = (w * WEIGHT_TOTAL) / total;
    return { id, n: Math.max(1, Math.floor(exact)), frac: exact - Math.floor(exact) };
  });
  let rest = WEIGHT_TOTAL - parts.reduce((s, p) => s + p.n, 0);
  // Con muchos asesores de parte chica los mínimos pueden pasarse de 10: se recorta a los más grandes.
  while (rest < 0) {
    const biggest = parts.reduce((a, b) => (b.n > a.n ? b : a));
    if (biggest.n <= 1) break;
    biggest.n -= 1;
    rest += 1;
  }
  const byRest = [...parts].sort((a, b) => b.frac - a.frac);
  for (let i = 0; rest > 0; i++, rest--) byRest[i % byRest.length]!.n += 1;
  return Object.fromEntries(parts.map((p) => [p.id, p.n]));
}

/** Sugerencia de pesos que suman 10 para n asesores (4/3/3, 3/3/2/2…). */
export function suggestWeights(advisorIds: readonly string[]): Record<string, number> {
  const n = advisorIds.length;
  const out: Record<string, number> = {};
  if (n === 0) return out;
  const base = Math.floor(WEIGHT_TOTAL / n);
  let rest = WEIGHT_TOTAL - base * n;
  for (const id of advisorIds) {
    out[id] = base + (rest > 0 ? 1 : 0);
    if (rest > 0) rest -= 1;
  }
  return out;
}

export interface RankingInput {
  advisorId: string;
  advisorName: string;
  metrics: FunnelMetrics;
  /** Leads activos vencidos (+7 días) y sin gestionar, para penalizar. */
  overdue: number;
  unmanaged: number;
  /** false = dado de baja o ya no es asesor de la empresa, pero tiene métricas en el mes (Lote 6 · I2). */
  active?: boolean;
  /** Rol en la empresa (ASESOR); null si ya no tiene membresía. */
  role?: string | null;
}

export interface RankingRow extends RankingInput {
  position: number;
  /** Puntaje transparente: ventas×10 + separaciones×5 + visitas×2 + citas concretadas×1 − vencidos +7 × 0.5 */
  points: number;
  /** % contacto efectivo del asesor: gestiones efectivas ÷ gestiones de contacto (la misma fórmula que el Embudo). */
  contactabilidad: number | null;
  active: boolean;
  role: string | null;
}

export const RANKING_WEIGHTS = {
  VENTAS: 10,
  SEPARACIONES: 5,
  VISITAS: 2,
  CITAS_CONCRETADAS: 1,
  OVERDUE_PENALTY: 0.5,
} as const;

/** Ranking de asesores: orden por puntaje y desempates por ventas, separaciones, visitas. */
export function rankAdvisors(rows: readonly RankingInput[]): RankingRow[] {
  const scored = rows.map((r) => ({
    ...r,
    active: r.active ?? true,
    // null se conserva: significa que ya no tiene membresía en la empresa (se fue); solo «sin dato» es ASESOR.
    role: r.role === undefined ? 'ASESOR' : r.role,
    position: 0,
    points:
      r.metrics.VENTAS * RANKING_WEIGHTS.VENTAS +
      r.metrics.SEPARACIONES * RANKING_WEIGHTS.SEPARACIONES +
      r.metrics.VISITAS * RANKING_WEIGHTS.VISITAS +
      r.metrics.CITAS_CONCRETADAS * RANKING_WEIGHTS.CITAS_CONCRETADAS -
      r.overdue * RANKING_WEIGHTS.OVERDUE_PENALTY,
    contactabilidad:
      r.metrics.CONTACTOS_TOTAL > 0
        ? r.metrics.CONTACTOS_EFECTIVOS / r.metrics.CONTACTOS_TOTAL
        : null,
  }));
  scored.sort(
    (a, b) =>
      b.points - a.points ||
      b.metrics.VENTAS - a.metrics.VENTAS ||
      b.metrics.SEPARACIONES - a.metrics.SEPARACIONES ||
      b.metrics.VISITAS - a.metrics.VISITAS ||
      a.advisorName.localeCompare(b.advisorName),
  );
  return scored.map((r, i) => ({ ...r, position: i + 1 }));
}

export interface QualityLead {
  id: string;
  originalMediumCategory: string | null;
  subStatus: string | null;
  utmMedium: string | null;
  isDigital: boolean;
  isRecontact: boolean;
}

export interface QualityNode {
  key: string;
  total: number;
  /** Conteo por agrupación de sub-estado (NO_GESTIONADO / DUPLICADO / GESTIONADO / OTRO). */
  groups: Record<string, number>;
}

export interface LeadQualityTree {
  totalDigital: number;
  byCategory: QualityNode[];
  byAudience: QualityNode[];
}

/**
 * Árbol de calidad de leads digitales: leads → categoría de medio → sub-estado, y leads → utm_medium (audiencia) → sub-estado.
 */
export function leadQualityTree(
  leads: readonly QualityLead[],
  catalog?: readonly SubStatusCatalogEntry[],
): LeadQualityTree {
  const digital = leads.filter((l) => l.isDigital);
  const build = (keyOf: (l: QualityLead) => string): QualityNode[] => {
    const map = new Map<string, QualityNode>();
    for (const l of digital) {
      const key = keyOf(l);
      const node = map.get(key) ?? { key, total: 0, groups: {} };
      node.total += 1;
      const g = subStatusGroup(l.subStatus, catalog).group;
      node.groups[g] = (node.groups[g] ?? 0) + 1;
      map.set(key, node);
    }
    return [...map.values()].sort((a, b) => b.total - a.total);
  };
  return {
    totalDigital: digital.length,
    byCategory: build((l) => l.originalMediumCategory ?? 'SIN_MEDIO'),
    byAudience: build((l) => l.utmMedium ?? 'SIN_AUDIENCIA'),
  };
}
